// 공지 상세 페이지
// - 상세 데이터 로드, 승인/발송 액션, 접근 권한 체크를 담당
// - 작성자/소속/대상 조직/타임스탬프를 한국어 라벨로 표기
import React, { useEffect, useMemo, useState } from 'react'
import DOMPurify from 'dompurify'
import { Link, useParams } from 'react-router-dom'
import { Roles } from '../auth/rbac'
import { useAccess } from '../auth/useAccess'
import { api } from '../lib/api'
import { formatDateTime } from '../lib/date'
import { NoticeStatusLabel } from '../lib/labels'
import InlineError from '../ui/InlineError'
import Loading from '../ui/Loading'
import { useToast } from '../ui/ToastProvider'

// const LEADER_ROLES = ['OPERATOR', 'GA_LEADER', 'INSURER_LEADER']

export default function NoticeDetailPage() {
    const { id } = useParams()
    const [detail, setDetail] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const { show } = useToast()
    const { user, allow } = useAccess()
    const [attachments, setAttachments] = useState([])

    // const hasRole = (roles = []) => Array.isArray(roles) && roles.some(r => LEADER_ROLES.includes(r))
    const canApprove = allow.role([
        Roles.OPERATOR,
        Roles.GA_LEADER,
        Roles.GA_MANAGER,
        Roles.INSURER_MASTER_ADMIN,
        Roles.INSURER_MASTER_DEPUTY_ADMIN,
        Roles.INSURER_LEADER,
        Roles.INSURER_DEPUTY_LEADER,
        Roles.INSURER_OTHER_ADMIN,
        Roles.INSURER_BRANCH_MANAGER,
        Roles.INSURER_DEPUTY_BRANCH_MANAGER,
    ])

    const canManageAttachments = allow.role([
        Roles.OPERATOR,
        Roles.GA_LEADER,
        Roles.GA_MANAGER,
        Roles.GA_PLANNER,
        Roles.INSURER_MASTER_ADMIN,
        Roles.INSURER_MASTER_DEPUTY_ADMIN,
        Roles.INSURER_LEADER,
        Roles.INSURER_DEPUTY_LEADER,
        Roles.INSURER_OTHER_ADMIN,
        Roles.INSURER_BRANCH_MANAGER,
        Roles.INSURER_DEPUTY_BRANCH_MANAGER,
    ])
    const sanitizedContent = useMemo(
        () => DOMPurify.sanitize(detail?.content || '', {
            USE_PROFILES: { html: true },
        }),
        [detail?.content]
    )

    useEffect(() => {
        let ignore = false
        async function load() {
            setLoading(true)
            setError(null)
            try {
                const res = await api.get(`/notices/${id}`)
                const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
                if (!ignore) setDetail(payload)

                // 읽음 기록(내부 동작): UI에 표시하지 않고 백그라운드로만 저장
                try {
                    await api.post(`/notices/${id}/read`, { deviceType: 'WEB' })
                } catch { }

                // 첨부 목록 로드
                try {
                    const a = await api.get(`/notices/${id}/attachments`)
                    const list = a?.data?.data ?? a?.data ?? []
                    if (!ignore) setAttachments(Array.isArray(list) ? list : [])
                } catch { }
            } catch (e) {
                if (!ignore) setError('상세를 불러오지 못했습니다')
            } finally {
                if (!ignore) setLoading(false)
            }
        }
        load()
        return () => { ignore = true }
    }, [id])

    if (loading) return <Loading />
    if (error) return <InlineError message={error} />
    if (!detail) return null

    const status = detail.status
    // 초안(DRAFT) 단계에서도 승인 권한이 있는 사용자는 바로 승인 가능하도록 허용
    const canApproveNow = canApprove && (status === 'DRAFT')
    const isCreator = detail?.createdById != null && user?.id != null && Number(detail.createdById) === Number(user.id)
    const canEdit = status === 'DRAFT' && (isCreator || canApprove)
    // 공지 "발송(카카오/SMS)" 기능은 현재 비활성(웹알람/앱푸시로 승인 즉시 알림)

    const onDeleteAttachment = async (att) => {
        if (!window.confirm('첨부를 삭제할까요?')) return
        try {
            await api.delete(`/notices/${detail.id}/attachments/${att.id}`)
            setAttachments(prev => prev.filter(x => x.id !== att.id))
            show('삭제되었습니다', 'success')
        } catch (err) {
            show('삭제 실패', 'error')
        }
    }

    return (
        <section>
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold">공지 상세 #{detail.id}</h1>
                <span className={`text-xs px-2 py-1 rounded border ${status === 'APPROVED' ? 'bg-green-50 border-green-200 text-green-700' : status === 'PENDING_APPROVAL' ? 'bg-yellow-50 border-yellow-200 text-yellow-700' : 'bg-slate-50 border-slate-200 text-slate-700'}`}>{NoticeStatusLabel[status] || status}</span>
            </div>

            <div className="rounded border p-4 mt-4 text-sm text-slate-700 space-y-2">
                <div className="text-lg font-semibold">{detail.title}</div>
                <div className="text-slate-600 text-xs">작성자 : {detail.createdByName || '-'} · 작성일 : {formatDateTime(detail.createdAt)} {detail.approvedAt ? `· 승인: ${formatDateTime(detail.approvedAt)}` : ''} {detail.sentAt ? `· 발송: ${formatDateTime(detail.sentAt)}` : ''}</div>
                <div className="text-slate-600 text-xs">소속 : {detail.orgName} {detail.targetOrgNames && Array.isArray(detail.targetOrgNames) && detail.targetOrgNames.length > 0 ? `· 대상: ${detail.targetOrgNames.join(', ')}` : (detail.targetOrgName ? `· 대상: ${detail.targetOrgName}` : '')}</div>
                <hr />
                <div
                    className="leading-6 prose max-w-none"
                    // 공지 본문 HTML은 렌더링 전에 sanitize 하여 저장형 XSS를 줄인다.
                    dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                />
            </div>

            {/* 첨부 패널 */}
            <div className="rounded border p-4 mt-4">
                <div className="flex items-center justify-between">
                    <h2 className="font-semibold">첨부파일</h2>
                </div>
                <ul className="mt-3 divide-y">
                    {attachments.map(att => (
                        <li key={att.id} className="py-2 flex items-center justify-between text-sm">
                            <div className="flex items-center gap-3">
                                <span className="text-slate-700">{att.filename}</span>
                                <span className="text-slate-400">{att.contentType || ''}</span>
                                <span className="text-slate-400">{att.size ? `${Math.round(att.size / 1024)} KB` : ''}</span>
                                {att.expired && (
                                  <span className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-0.5">
                                    다운로드 기간만료
                                  </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                {!att.expired && (
                                  <>
                                    <button
                                      type="button"
                                      className="underline text-slate-700"
                                      onClick={async () => {
                                        try {
                                          const res = await api.get(`/files/${att.fileId}/preview`, { responseType: 'blob' })
                                          const url = URL.createObjectURL(res.data)
                                          window.open(url, '_blank')
                                          URL.revokeObjectURL(url)
                                        } catch { }
                                      }}
                                    >
                                      미리보기
                                    </button>
                                    <button
                                      type="button"
                                      className="underline text-slate-700"
                                      onClick={async () => {
                                        try {
                                          const res = await api.get(`/files/${att.fileId}/preview`, { responseType: 'blob' })
                                          const blobUrl = URL.createObjectURL(res.data)
                                          const a = document.createElement('a')
                                          a.href = blobUrl
                                          a.download = att.filename || att.fileId || 'download'
                                          document.body.appendChild(a)
                                          a.click()
                                          a.remove()
                                          URL.revokeObjectURL(blobUrl)
                                        } catch { }
                                      }}
                                    >
                                      다운로드
                                    </button>
                                  </>
                                )}
                                {canManageAttachments && (
                                    <button type="button" className="text-red-600" onClick={() => onDeleteAttachment(att)}>삭제</button>
                                )}
                            </div>
                        </li>
                    ))}
                    {attachments.length === 0 && (
                        <li className="py-4 text-sm text-slate-500">첨부가 없습니다.</li>
                    )}
                </ul>
            </div>

            <div className="mt-4 flex items-center justify-between">
                <Link to="/notices" className="rounded border px-3 py-2 text-sm">목록</Link>
                <div className="flex gap-2">
                    {canEdit && (
                        <Link to={`/notices/${id}/edit`} className="rounded border px-3 py-2 text-sm">
                            수정
                        </Link>
                    )}
                    {canApprove && (
                                <button
                                    className="rounded border px-3 py-2 text-sm disabled:opacity-50"
                                    disabled={!canApproveNow}
                                    onClick={async () => {
                                        try {
                                            await api.patch(`/notices/${id}/approve`)
                                            show('승인 완료', 'success')
                                            const res = await api.get(`/notices/${id}`)
                                            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
                                            setDetail(payload)
                                        } catch {
                                            show('승인 실패', 'error')
                                        }
                                    }}
                                >
                            승인
                        </button>
                    )}
                    {/* 발송 버튼은 현재 숨김(향후 외부 발송 연동 시 feature flag로 노출) */}
                </div>
            </div>
        </section>
    )
}


